import get from 'lodash/get';
import ValueSet from '../ValueSet';
import { uspsStates, iso3166CountryCodes, yesNo } from './predefinedValueSets';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import SurveyItem from '../SurveyItem';

interface ValueSetHashMap {
  [valueSetId: string]: any;
}

interface ValueSetReferences {
  [valueSetId: string]: SurveyItem[];
}

const systemValueSets = [uspsStates, iso3166CountryCodes, yesNo];

export type AddExternalValueSetResult = {
  updatedValueSets: fhir.ValueSet[];
  ingestedValueSetId: null | string;
};

export default class ValueSets {
  private _valueSetHashMap: ValueSetHashMap = {};
  private _valueSetList: ValueSet[];
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  private _lazyValueSetReferences: ValueSetReferences = null;

  /**
   * the non system-provided value sets
   */
  _customValueSets: ValueSet[];

  constructor(fhirQuestionnaire: fhir.Questionnaire) {
    const questionnaireContained = get<fhir.Questionnaire, 'contained', any[]>(
      fhirQuestionnaire,
      'contained',
      [],
    );
    const fhirValueSets = questionnaireContained.filter(
      (continedResource = {} as any) =>
        continedResource.resourceType === 'ValueSet',
    ) as fhir.ValueSet[];
    this._customValueSets = fhirValueSets.map((fhirValueSet: fhir.ValueSet) =>
      ValueSet.initFromFhirResource(fhirValueSet),
    );

    // put value sets in hash map for quicker lookup
    this._valueSetList = [...systemValueSets, ...this._customValueSets].sort(
      (valueSetA, valueSetB) => {
        if (
          !valueSetA ||
          !valueSetB ||
          !valueSetA.name ||
          !valueSetB.name ||
          !isString(valueSetA.name) ||
          !isString(valueSetB.name)
        ) {
          return 0;
        }

        return valueSetA.name.localeCompare(valueSetB.name);
      },
    );
    this._valueSetList.forEach((valueSet: ValueSet) => {
      this._valueSetHashMap[valueSet.id] = valueSet;
    });
  }

  /**
   * Get lazily initialized value set references
   */
  private getValueSetReferences(surveyItems: SurveyItem[] = []) {
    if (this._lazyValueSetReferences === null) {
      this._lazyValueSetReferences = {};
      surveyItems.forEach(surveyItem => {
        if (!surveyItem.optionReference) return;

        const existingReferences =
          this._lazyValueSetReferences[surveyItem.optionReference] || [];
        this._lazyValueSetReferences[surveyItem.optionReference] = [
          ...existingReferences,
          surveyItem,
        ];
      });
    }

    return this._lazyValueSetReferences;
  }

  public getValueSetReferenceCount(
    valueSetId: string,
    surveyItems: SurveyItem[],
  ) {
    const valueSetReferences = this.getValueSetReferences(surveyItems);
    return (valueSetReferences[valueSetId] || []).length;
  }

  /**
   * Return a value set with the specified key
   *
   * @memberof ValueSets
   */
  getValueSetWithId = (id: string = ''): ValueSet => {
    // pound sign denotes a resource-internal value set, irrelevant for this implementation
    let normalizedId = id;
    if (id[0] === '#') {
      normalizedId = id.substr(1);
    }

    return this._valueSetHashMap[normalizedId] || null;
  };

  cloneAndAddValueSet = (valueSet: ValueSet): fhir.ValueSet[] => {
    return [
      ...this._customValueSets.map(valueSet => valueSet.fhirResource),
      valueSet.fhirResource,
    ];
  };

  cloneAndRemoveValueSet = (id: string): fhir.ValueSet[] => {
    return this._customValueSets
      .filter(existingValueSet => existingValueSet.id !== id)
      .map(valueSet => valueSet.fhirResource);
  };

  /**
   * Add an external valueset from another survey.  This ensures duplicates aren't created and any
   * id collisions are avoided
   */
  addExternalValueSet(externalValueSet: ValueSet): AddExternalValueSetResult {
    const existingValueSet = this.getValueSetWithId(externalValueSet.id);
    if (existingValueSet && existingValueSet.equals(externalValueSet)) {
      // value set already exists, don't ingest duplicates
      return {
        updatedValueSets: this._customValueSets.map(_ => _.fhirResource),
        ingestedValueSetId: existingValueSet.id,
      };
    }

    // ensure that their isn't a naming collision
    let updatedName = externalValueSet.name;
    while (this.doesNameAlreadyExist(updatedName)) {
      updatedName = `${updatedName} (copy)`;
      if (updatedName.length > 10000) {
        // should never happen, but just to be safe
        console.error('Unable to generate valid valueset name name');
        return {
          updatedValueSets: this._customValueSets.map(_ => _.fhirResource),
          ingestedValueSetId: null,
        };
      }
    }

    // Update new valueset with ensured deduped name
    const updatedWithValidName = externalValueSet.updateName(updatedName);
    const updatedValueSets = this.cloneAndAddValueSet(updatedWithValidName);

    return {
      updatedValueSets,
      ingestedValueSetId: updatedWithValidName.id,
    };
  }

  get valueSetList(): ValueSet[] {
    return this._valueSetList;
  }

  /**
   * The id of the default valueset to use, returns null if no defaul could be determined
   */
  get defaultValueSetId(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this._valueSetList.length) return null;

    return this._valueSetList[0].id;
  }

  /**
   * Checks if name already exists in the value set collection.
   * Note this compares name by derived ID.  A name could have variantions on casing and white space
   * but will still qualify as being the same.
   * @memberof ValueSets
   */
  doesNameAlreadyExist = (name: string): boolean => {
    const derivedId = ValueSet.generateIdFromName(name);

    if (isNil(derivedId)) return false;

    return !!this.getValueSetWithId(derivedId);
  };

  /**
   * Determines if the target valueset is a system valueset or a user-created value set
   */
  isSystemValueSet(valueSetId: string): boolean {
    return !!systemValueSets.find(valueSet => valueSet.id === valueSetId);
  }
}
